import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  convertRemToPx,
  useTheme,
  useBreakpoint,
} from '@hivebrite/design-system/Theme';
import type { UseBreakpointProps } from '@hivebrite/design-system/Theme';

import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { RootItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/rootItemAttributes';

import { HEADINGS_VALUES } from './utils/constants';

const Root = () => <div />; // never used

const DEFAULT_LINK_COLOR = '#4733FF';
const useLinkColorFromTheme = () => {
  const theme = useTheme();

  return theme.colors?.link || DEFAULT_LINK_COLOR;
};

const RenderBuilderInner = ({ children }) => {
  const linkColor = useLinkColorFromTheme();
  const breakpoint = useBreakpoint();

  return (
    <ResetStyledRoot
      data-testid="root-builder-component"
      $breakpoint={breakpoint}
      $linkColor={linkColor}
      $unit="rem"
    >
      {children}
    </ResetStyledRoot>
  );
};

const RenderBuilder = ({ children }) => (
  <BuilderThemeProvider viewAs={VIEW_AS.USER}>
    <RenderBuilderInner>{children}</RenderBuilderInner>
  </BuilderThemeProvider>
);

const Render = ({
  children,
}: RenderProps<DocumentItem<RootItemAttributes>>) => {
  const linkColor = useLinkColorFromTheme();
  const breakpoint = useBreakpoint();

  return (
    <ResetStyledRoot
      data-testid="root-component"
      $breakpoint={breakpoint}
      $linkColor={linkColor}
      $unit="rem"
    >
      {children}
    </ResetStyledRoot>
  );
};

Root.componentName = 'Root';
Root.componentType = 'root';

Root.canBeDoubleClicked = false;
Root.canBeDragged = false;
Root.canBeListed = false;
Root.canBeSelected = false;

Root.RenderBuilder = RenderBuilder;

Root.Render = Render;

export default Root;

interface HeadingsDefaultStyleProps {
  breakpoint: UseBreakpointProps;
  unit: 'rem' | 'px';
}

// we convert rem to px because we use those in email_renderer
export const headingsDefaultStyle = ({
  breakpoint,
  unit,
}: HeadingsDefaultStyleProps) => {
  const device = ['s', 'xs', 'm'].includes(breakpoint)
    ? 'isMobile'
    : 'isDesktop';

  return `
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: inherit;
      font-family: inherit;
      margin-left: 0;
      margin-right: 0;
    }

    h1 {
      font-size: ${HEADINGS_VALUES[unit][device].h1.fontSize};
      line-height: ${HEADINGS_VALUES[unit][device].h1.lineHeight};
    }

    h2 {
      font-size: ${HEADINGS_VALUES[unit][device].h2.fontSize};
      line-height: ${HEADINGS_VALUES[unit][device].h1.lineHeight};
    }

    h3 {
      font-size: ${HEADINGS_VALUES[unit][device].h3.fontSize};
      line-height:  ${HEADINGS_VALUES[unit][device].h3.lineHeight};
    }
    h4 {
      margin-top: ${convertRemToPx(1.33)}px;
      margin-bottom: ${convertRemToPx(1.33)}px;
    }

    h5 {
      margin-top: ${convertRemToPx(1.67)}px;
      margin-bottom: ${convertRemToPx(1.67)}px;
    }

    h6 {
      margin-top: ${convertRemToPx(2.33)}px;
      margin-bottom: ${convertRemToPx(2.33)}px;
    }

    // We need to override FO/PO style on sup to match the BO style (default style)
    sup {
      vertical-align: super !important;
      font-size: smaller !important;
    }
  `;
};

interface ResetStyledRootProps {
  $breakpoint: HeadingsDefaultStyleProps['breakpoint'];
  $unit: HeadingsDefaultStyleProps['unit'];
  $linkColor: string;
}

const ResetStyledRoot = styled(Box).attrs(() => ({
  color: 'black',
  flex: 1,
  flexDirection: 'column',
  fontSize: 2,
  width: '100%',
}))<ResetStyledRootProps>`
  ${(props) =>
    headingsDefaultStyle({
      breakpoint: props.$breakpoint,
      unit: props.$unit,
    })}
  *::selection {
    // selection color has to be transparent to display underline/strikethrough style on Chrome.
    background: rgba(71, 51, 255, 0.4) !important;
    color: initial !important;
  }

  a {
    color: ${({ $linkColor }) => $linkColor};
    text-decoration: underline;
  }

  strong {
    font-weight: bold;
  }

  em {
    font-style: italic;
  }

  s {
    text-decoration: line-through;
  }

  u {
    text-decoration: underline;
  }
`;
